import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ArrowLeft, AlertCircle, Mail, Phone, UserCheck, ShieldCheck, Edit3, Building2, MapPin } from 'lucide-react';
import { useOnboardingStore } from '../../store';
import { sanitizeInput, validateEmail, validatePhone } from '../../lib/validation';
import { isDeveloperMode, devModeData } from '../../config/devMode';

interface ContactConfirmForm {
  contactName: string;
  email: string;
  phone: string;
}

export function AccountCreation() {
  const { client, setStep, updateClient } = useOnboardingStore();
  const [error, setError] = React.useState<string | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const { register, handleSubmit, formState: { errors }, setValue, control } = useForm<ContactConfirmForm>({
    defaultValues: {
      contactName: client.contactName || (isDeveloperMode ? devModeData.account.fullName : ''),
      email: client.email || (isDeveloperMode ? devModeData.account.email : ''),
      phone: client.phone || (isDeveloperMode ? devModeData.account.phone : ''),
    },
  });
  const watchedContactFields = useWatch({ control });

  React.useEffect(() => {
    if (client.contactName !== undefined) setValue('contactName', client.contactName || '');
    if (client.email !== undefined) setValue('email', client.email || '');
    if (client.phone !== undefined) setValue('phone', client.phone || '');
  }, [client.contactName, client.email, client.phone, setValue]);

  React.useEffect(() => {
    const hasWatchedValue = [
      watchedContactFields.contactName,
      watchedContactFields.email,
      watchedContactFields.phone
    ].some((value) => value !== undefined);
    if (!hasWatchedValue) return;

    updateClient({
      contactName: sanitizeInput(watchedContactFields.contactName || ''),
      email: watchedContactFields.email ? sanitizeInput(watchedContactFields.email.toLowerCase().trim()) : '',
      phone: sanitizeInput(watchedContactFields.phone || ''),
    });
  }, [
    watchedContactFields.contactName,
    watchedContactFields.email,
    watchedContactFields.phone,
    updateClient
  ]);

  const onSubmit = (data: ContactConfirmForm) => {
    try {
      setError(null);
      const contactName = sanitizeInput(data.contactName || '');
      const email = sanitizeInput((data.email || '').toLowerCase().trim());
      const phone = sanitizeInput(data.phone || '');

      if (!contactName) throw new Error('Contact name is required');
      if (!validateEmail(email)) throw new Error('Enter a valid email address');
      if (phone && !validatePhone(phone)) throw new Error('Enter a valid phone number');

      updateClient({ contactName, email, phone });
      setIsEditing(false);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not confirm contact details');
    }
  };

  const contactReady = Boolean(client.contactName && client.email && client.phone);
  const showReview = contactReady && !isEditing;

  const handleConfirmExisting = () => {
    const contactName = sanitizeInput(client.contactName || '');
    const email = sanitizeInput((client.email || '').toLowerCase().trim());
    const phone = sanitizeInput(client.phone || '');

    if (!contactName) {
      setIsEditing(true);
      setError('Add the setup contact name before continuing');
      return;
    }
    if (!validateEmail(email)) {
      setIsEditing(true);
      setError('Enter a valid email address');
      return;
    }
    if (!validatePhone(phone)) {
      setIsEditing(true);
      setError('Enter a valid phone number');
      return;
    }

    updateClient({ contactName, email, phone });
    setError(null);
    setStep(2);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#22c55e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <UserCheck className="w-8 h-8 text-[#22c55e]" />
        </div>
        <h2 className="text-2xl font-semibold text-white mb-2">Review where setup details should go</h2>
        <p className="text-gray-400 leading-6">
          We carried these forward from the first step. Edit only if the setup updates should go somewhere else.
        </p>
      </div>

      {(client.businessName || client.address) && (
        <div className="bg-scale-darker bg-opacity-50 backdrop-blur-sm border border-gray-700 p-5 rounded-lg mb-8">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#22c55e] mt-0.5 flex-shrink-0" />
            <div className="text-left">
              <h3 className="font-semibold text-white">We will save this as you go</h3>
              <p className="text-sm text-gray-300 mt-1">
                Business and contact details stay synced into Connect, the dashboard tracker, and the final provisioning handoff.
              </p>
              {client.businessName && <p className="text-sm text-gray-400 mt-3">Business: <span className="text-gray-200">{client.businessName}</span></p>}
            </div>
          </div>
        </div>
      )}

      {showReview && (
        <div className="mb-8 grid gap-3">
          {[
            { icon: Building2, label: 'Business', value: client.businessName || 'Not entered' },
            { icon: UserCheck, label: 'Setup contact', value: client.contactName },
            { icon: Mail, label: 'Email', value: client.email },
            { icon: Phone, label: 'Phone', value: client.phone },
            { icon: MapPin, label: 'Business area', value: client.address || 'Not entered' },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-start gap-3 rounded-lg border border-gray-700 bg-scale-darker/70 p-4 text-left">
              <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-scale-blue/10">
                <Icon className="h-4 w-4 text-scale-blue" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
                <p className="mt-1 break-words text-sm font-medium text-white">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {showReview ? (
        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => setStep(0)}
            className="flex items-center justify-center rounded-md bg-gray-700 px-6 py-2 font-semibold text-white transition-colors hover:bg-gray-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </button>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center justify-center rounded-md border border-gray-600 px-5 py-2 font-semibold text-gray-100 transition-colors hover:bg-white/10"
            >
              <Edit3 className="mr-2 h-4 w-4" />
              Edit details
            </button>
            <button
              type="button"
              onClick={handleConfirmExisting}
              className="rounded-md bg-scale-blue px-6 py-2 font-semibold text-scale-dark transition-colors hover:bg-white"
            >
              Looks right, continue
            </button>
          </div>
        </div>
      ) : (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-white mb-1">Contact Name *</label>
          <input
            type="text"
            {...register('contactName', { required: 'Contact name is required' })}
            className="mt-1 block w-full rounded-md bg-scale-darker border-gray-700 text-gray-100 placeholder-gray-500 focus:border-scale-blue focus:ring-scale-blue"
            placeholder="Who should receive setup updates?"
          />
          {errors.contactName && <p className="mt-1 text-sm text-red-400">{errors.contactName.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Email Address *</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className="block w-full pl-10 pr-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-scale-blue focus:border-transparent"
              placeholder="name@business.com"
            />
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Phone Number *</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="tel"
              {...register('phone', { required: 'Phone number is required' })}
              className="block w-full pl-10 pr-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-scale-blue focus:border-transparent"
              placeholder="Best phone number"
            />
          </div>
          {errors.phone && <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>}
        </div>

        <div className="flex justify-between items-center space-x-4 pt-4">
          <button
            type="button"
            onClick={() => setStep(0)}
            className="w-32 flex items-center justify-center px-6 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors font-semibold"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-2 bg-scale-blue text-scale-dark rounded-md hover:bg-scale-blue/90 transition-colors font-semibold"
          >
            Confirm and continue
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
